import type { ReactElement } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Circle } from 'lucide-react';

interface StatusDotProps {
  isActive: boolean;
  activeLabel?: string;
  inactiveLabel?: string;
  tooltip?: string;
  showLabel?: boolean;
  /** Alignment when rendered inside a flex container (e.g. tooltip wrapper). Default 'center'. */
  align?: 'start' | 'center';
}

/**
 * Status indicator dot with optional label and tooltip
 * Used for showing active/inactive, running/offline states
 */
export function StatusDot({
  isActive,
  activeLabel = 'Active',
  inactiveLabel = 'Inactive',
  tooltip,
  showLabel = true,
  align = 'center',
}: StatusDotProps): ReactElement {
  const content = (
    <div className="flex items-center gap-1">
      <Circle
        className={`h-2 w-2 ${
          isActive
            ? 'fill-green-500 text-green-500'
            : 'fill-gray-300 text-gray-300'
        }`}
      />
      {showLabel && (
        <span className="text-xs text-muted-foreground">
          {isActive ? activeLabel : inactiveLabel}
        </span>
      )}
    </div>
  );

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`flex items-center ${align === 'start' ? 'justify-start' : 'justify-center'}`}
            >
              {content}
            </div>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}
